<!-- PricingComponent.vue -->
<template>
  <div class="page-content mdl-shadow--2dp is-casting-shadow">
    <div class="row strat-config align-items-center">
      <div class="col-lg-6 col-md-6 col-sm-4 col-xs-12">
        <div class="current-strategy d-flex align-items-center">
          <span class="fa-stack fa-lg">
            <i
              class="fa fa-circle fa-stack-2x"
              :style="{ color: currentStrategy.color }"
            ></i>
            <span class="fa fa-stack-1x strat-abbr">C</span>
          </span>
          <span class="strat-name ms-2">{{ currentStrategy.name }}</span>
        </div>
      </div>
      <div
        class="col-lg-6 col-md-6 col-sm-8 col-xs-12 d-flex justify-content-end align-items-center"
      >
        <div class="pricing-configuration">
          <div class="selector">
            <label
              class="mdl-radio mdl-js-radio mdl-js-ripple-effect"
              for="option-1"
            >
              <input
                type="radio"
                id="option-1"
                class="mdl-radio__button"
                name="options"
                v-model="selectedStrategyType"
                :value="1"
                @change="handleStrategyChange"
              />
              <span class="mdl-radio__label">
                Apply same rules to all days (Mon-Sun)
              </span>
            </label>

            <label
              class="mdl-radio mdl-js-radio mdl-js-ripple-effect mt-2"
              for="option-2"
            >
              <input
                type="radio"
                id="option-2"
                class="mdl-radio__button"
                name="options"
                v-model="selectedStrategyType"
                :value="2"
                @change="handleStrategyChange"
              />
              <span class="mdl-radio__label"> Apply rules per day of week</span>
            </label>

            <label
              class="mdl-radio mdl-js-radio mdl-js-ripple-effect mt-2"
              for="option-3"
            >
              <input
                type="radio"
                id="option-3"
                class="mdl-radio__button"
                name="options"
                v-model="selectedStrategyType"
                :value="3"
                @change="handleStrategyChange"
              />
              <span class="mdl-radio__label">
                Apply rules down to the hour
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>
    <PricingAccordionComponent
      :strategy="strategy"
      :strategyType="strategyType"
      @season-updated="onSeasonUpdated"
      @save-strategy="onSaveStrategy"
    />
    <ConfirmModal
      :show="showConfirmModal"
      message="Changing this setting will remove your current utilization settings."
      prompt="Are you sure you wish to continue?"
      confirmText="Yes"
      cancelText="No"
      confirmType="confirm-modal-btn-primary"
      cancelType="confirm-modal-btn-danger"
      @confirm="onConfirmModal"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, toRef } from 'vue';
import PricingAccordionComponent from './PricingAccordion/PricingAccordionComponent.vue';
import ConfirmModal from './ConfirmModal.vue';

import { Strategy } from '@/api';
import { strategyApi } from '@/apiClient';
import eventBus, { EventType } from '@/eventBus';

export default defineComponent({
  components: {
    PricingAccordionComponent,
    ConfirmModal,
  },
  name: 'PricingComponent',
  props: {
    strategy: {
      type: Object as PropType<Strategy>,
      required: true,
    },
  },
  setup(props, { emit }) {
    const currentStrategy = toRef(props, 'strategy');
    const strategyType = ref(
      currentStrategy.value.isSetByHour
        ? 3
        : currentStrategy.value.isSetByDayOfWeek
        ? 2
        : 1
    );
    const selectedStrategyType = ref(strategyType.value);
    const pendingStrategyType = ref<number | null>(null);
    const showConfirmModal = ref(false);

    const onSeasonUpdated = () => {
      emit('season-updated');
    };

    const handleStrategyChange = (e: Event) => {
      const target = e.target as HTMLInputElement;
      const value = parseInt(target.value, 10);
      pendingStrategyType.value = value;
      selectedStrategyType.value = strategyType.value;
      showConfirmModal.value = true;
    };

    const onConfirmModal = (confirmed: boolean) => {
      showConfirmModal.value = false;
      if (confirmed && pendingStrategyType.value !== null) {
        strategyType.value = pendingStrategyType.value;
        selectedStrategyType.value = pendingStrategyType.value;

        if (strategyType.value === 1) {
          currentStrategy.value.isSetByDayOfWeek = false;
          currentStrategy.value.isSetByHour = false;
        } else if (strategyType.value === 2) {
          currentStrategy.value.isSetByDayOfWeek = true;
          currentStrategy.value.isSetByHour = false;
        } else if (strategyType.value === 3) {
          currentStrategy.value.isSetByDayOfWeek = true;
          currentStrategy.value.isSetByHour = true;
        }
      } else {
        selectedStrategyType.value = strategyType.value;
      }
      pendingStrategyType.value = null;
    };

    const onSaveStrategy = () => {
      console.log('Saving strategy', currentStrategy.value);
      strategyApi.apiStrategyPut(currentStrategy.value).then(() => {
        console.log('Strategy saved');
        eventBus.emit(EventType.RefreshStrategies);
      });
    };

    return {
      strategyType,
      selectedStrategyType,
      currentStrategy,
      onSeasonUpdated,
      onSaveStrategy,
      handleStrategyChange,
      showConfirmModal,
      onConfirmModal,
    };
  },
});
</script>

<style scoped>
/* TODO */
.page-content {
  padding: 20px;
  background: #fff;
  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 3px 1px -2px rgba(0, 0, 0, 0.2),
    0 1px 5px 0 rgba(0, 0, 0, 0.12);
}

.page-title-row {
  text-align: center;
  margin-bottom: 20px;
}

.title-divider {
  margin-top: 10px;
  border: none;
  border-top: 1px solid #ddd;
}

.page-title {
  font-size: 24px;
  font-weight: bold;
}

.strat-config {
  margin-bottom: 20px;
}

.current-strategy {
  display: flex;
  align-items: center;
}

.fa-stack {
  margin-right: 10px;
}

.selector {
  display: flex;
  flex-direction: column;
  text-align: left;
}

.mdl-radio {
  margin-bottom: 10px;
}
</style>
